<template>
    <div id="exception-calendar">
        <ModalEvents
            isException
            @updateEvents="getEvents"
        />
        <div
            class="h-100"
            v-if="!exception.exceptionCalendarExists"
        >
            <div class="no-items h-100">
                <EmptyBoxIcon class="empty-box" />
                <p class="title">{{ $t("CalendarsPage.NoEventTitle") }}</p>
                <p class="subtitle">
                    {{ $t("CalendarsPage.NoEventSubtitle") }}
                </p>
                <b-button
                    class="btn-new-event"
                    @click="exceptionPermissions.find((e) => e.type === 'create') && newEvent()"
                >
                    <AddIcon class="add-icon" />
                    {{ $t("CalendarsPage.NewEvent") }}
                </b-button>
            </div>
        </div>
        <div
            v-else
            class="calendar-body"
        >
            <div class="calendar-header">
                <div class="first-line">
                    <div class="title">
                        {{ $t("CalendarsPage.ExceptionCalendars") }}
                    </div>
                    <div class="add-events">
                        <sys-switch
                            size="md"
                            :value="filter.onlyFutureEvents"
                            :label="$t('CalendarsPage.OnlyFutureEvents')"
                            @input="(value) => (filter.onlyFutureEvents = value)"
                        />
                        <div class="buttons">
                            <b-button
                                id="filter-popover"
                                :class="[
                                    'btn-filter',
                                    exception.filters.length > 0 ? '--has-filters' : '',
                                    windowWidth < 680 && windowWidth > 480
                                        ? '--show-only-icons'
                                        : ''
                                ]"
                            >
                                <FilterIcon class="filter-icon" />
                                <span v-if="windowWidth >= 680 || windowWidth <= 480">
                                    {{
                                        `${$t("CalendarsPage.Filters")} ${
                                            exception.filters.length > 0
                                                ? `(${exception.filters.length})`
                                                : ""
                                        }`
                                    }}
                                </span>
                            </b-button>
                            <b-button
                                :class="[
                                    'btn-new-event',
                                    windowWidth < 680 && windowWidth > 480
                                        ? '--show-only-icons'
                                        : ''
                                ]"
                                @click="
                                    exceptionPermissions.find((e) => e.type === 'create') &&
                                    newEvent()
                                "
                            >
                                <AddIcon class="add-icon" />
                                <span v-if="windowWidth >= 680 || windowWidth <= 480">
                                    {{ $t("CalendarsPage.NewEvent") }}
                                </span>
                            </b-button>

                            <PopoverFilter
                                target="filter-popover"
                                placement="bottomleft"
                                custom-position="action-button"
                                triggers="click"
                                :windowWidth="windowWidth"
                            />
                        </div>
                    </div>
                </div>
                <div class="second-line">
                    <div class="search-input">
                        <div class="div-custom-input input__materials">
                            <div class="icon-block">
                                <SearchIcon class="search-icon" />
                            </div>
                            <b-form-tags
                                class="input-search-calendars"
                                ref="formTagMaterials"
                                v-model="filter.textFilter"
                                duplicate-tag-text=""
                                :placeholder="
                                    filter.textFilter.length === 0
                                        ? $t('SearchPlaceholderDefault')
                                        : ''
                                "
                                @input="getEvents()"
                                remove-on-delete
                                add-on-change
                            />
                        </div>
                    </div>
                    <div
                        v-if="exception.filters.length > 0"
                        class="filter-row"
                    >
                        <b-button
                            class="btn-clear"
                            @click="UPDATE_FILTERS_EXCEPTION([])"
                        >
                            {{ $t("Operator.CleanFilter") }}
                        </b-button>
                        <div
                            v-for="(filter, index) in exception.filters"
                            :key="filter.value"
                            class="filter"
                        >
                            {{ filter.value }}
                            <CloseIcon
                                @click="removeFilter(index)"
                                class="remove-icon"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="
                    exception.exceptionCalendarExists &&
                    exception.monthGroupsWithRecurrency.length === 0 &&
                    noItemsFound
                "
                class="block-no-results"
            >
                <div class="no-results">
                    <SearchIcon />
                    <p class="text">{{ $t("NoResultsFound") }}</p>
                </div>
            </div>
            <div
                v-else
                class="event-block"
            >
                <EventMonth
                    :monthGroups="exception.monthGroupsWithRecurrency"
                    @updateEventList="getEvents"
                    @removeRecurrency="getEvents"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { createRecurringEvents } from "@/views/pages/calendar/utils/recurrence.js";
    import { mapMutations, mapState, mapGetters } from "vuex";
    import EmptyBoxIcon from "@core/assets/icons/empty-box-icon.svg";
    import AddIcon from "@core/assets/icons/add-icon.svg";
    import FilterIcon from "@core/assets/icons/filter-icon.svg";
    import SearchIcon from "@core/assets/icons/search-icon.svg";
    import { BButton, BFormTags, BFormCheckbox } from "bootstrap-vue";
    import ToastificationContent from "@/@core/components/toastification/ToastificationContent.vue";
    import ModalEvents from "../actionCalendar/components/settings/ModalEvents";
    import EventMonth from "./components/EventMonth.vue";
    import CloseIcon from "@core/assets/icons/close-rounded-icon.svg";
    import PopoverFilter from "./components/PopoverFilter.vue";
    import SysSwitch from "@/@core/components/sys-switch";
    import { getCalendarExceptionContextService } from "./services";

    export default {
        components: {
            EmptyBoxIcon,
            AddIcon,
            FilterIcon,
            SearchIcon,
            CloseIcon,
            SysSwitch,
            BButton,
            BFormTags,
            BFormCheckbox,
            PopoverFilter,
            ToastificationContent,
            ModalEvents,
            EventMonth
        },
        data() {
            return {
                noItemsFound: true,
                filter: {
                    textFilter: [],
                    onlyFutureEvents: true
                }
            };
        },
        computed: {
            ...mapGetters("auth", ["getUserInfo", "getPermissions"]),

            ...mapState("calendar", {
                exception: "exception",
                exceptionPermissions: "exceptionPermissions",
                helpers: "helpers"
            }),
            ...mapState("app", ["windowWidth"]),

            translatedMonths() {
                const userInfo = this.getUserInfo;
                const language = userInfo?.language || "pt-BR";

                const translations = {
                    "en-US": [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec"
                    ],
                    "pt-BR": [
                        "Jan",
                        "Fev",
                        "Mar",
                        "Abr",
                        "Mai",
                        "Jun",
                        "Jul",
                        "Ago",
                        "Set",
                        "Out",
                        "Nov",
                        "Dez"
                    ]
                };

                return translations[language] || translations["en-US"];
            }
        },
        async mounted() {
            this.UPDATE_FLAG_SHOW_OVERLAY(true);

            await getCalendarExceptionContextService(this, false);

            this.handleCreateYearsContext();
            this.formatPermissionsByPage();
            await this.getEvents();

            this.UPDATE_FLAG_SHOW_OVERLAY(false);
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            ...mapMutations("calendar", [
                "UPDATE_MODAL_EVENTS_TOGGLE",
                "UPDATE_FILTERS_EXCEPTION",
                "UPDATE_CONTEXT_RESOURCES",
                "UPDATE_EXCEPTION_EVENT_TYPES_TOGGLE",
                "UPDATE_PERMISSIONS_EXCEPTION_LIST",
                "UPDATE_DURATION_UNITS_TOGGLE",
                "UPDATE_HELPERS_CONTEXT_YEARS",
                "UPDATE_MONTH_GROUPS_EXCEPTION",
                "UPDATE_EXCEPTION_CALENDAR_EXISTS",
                "UPDATE_MONTH_GROUPS_WITH_RECURRENCY_EXCEPTION",
                "UPDATE_MODAL_EVENT_ID"
            ]),

            async newEvent() {
                await getCalendarExceptionContextService(this);
                this.UPDATE_MODAL_EVENTS_TOGGLE(true);
                this.UPDATE_MODAL_EVENT_ID(null);
            },

            handleCreateYearsContext() {
                let selectedYear = new Date().getFullYear();
                let years = [];
                let yearRange = 100;
                let firstYear = selectedYear - Math.floor(yearRange / 2);
                let lastYear = firstYear + yearRange - 1;

                for (let year = firstYear; year <= lastYear; year++) {
                    years.push(year);
                }

                this.UPDATE_HELPERS_CONTEXT_YEARS(years);
            },

            removeFilter(index) {
                const filters = [...this.exception.filters];
                filters.splice(index, 1);
                this.UPDATE_FILTERS_EXCEPTION(filters);
            },

            async getEvents() {
                const textFilterQuery = this.filter.textFilter;

                if (!this.exceptionPermissions.find((e) => e.type === "show")) {
                    this.noItemsFound = !!textFilterQuery.length;
                    return;
                }

                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                const { queryStrings, result } = this.parseFilters(this.exception.filters);

                try {
                    const response = await this.$http2.get(
                        `/api/myconfig/exception-calendar?TextFilter=${textFilterQuery}&${queryStrings}&OnlyFutureEvents=${this.filter.onlyFutureEvents}`
                    );

                    this.noItemsFound =
                        !!textFilterQuery.length ||
                        !!this.exception.filters.length ||
                        !!this.filter.onlyFutureEvents;

                    const monthGroups = response.data.data.monthGroups.sort((a, b) => {
                        if (a.year !== b.year) {
                            return a.year - b.year;
                        }
                        return a.month - b.month;
                    });

                    this.UPDATE_EXCEPTION_CALENDAR_EXISTS(
                        !!response.data.data?.exceptionCalendarExists
                    );
                    this.UPDATE_MONTH_GROUPS_EXCEPTION(monthGroups);

                    const allEvents = monthGroups.flatMap((monthGroup) => monthGroup.events);

                    this.lastFilterApplied = {
                        ...result,
                        onlyFutureEvents: this.filter.onlyFutureEvents
                    };

                    const recurringEvents = createRecurringEvents(
                        allEvents,
                        2074,
                        this.formatRecurringFilter({ ...this.lastFilterApplied })
                    );

                    const monthGroupsAdapted = this.adaptEvents(recurringEvents);
                    this.UPDATE_MONTH_GROUPS_WITH_RECURRENCY_EXCEPTION(monthGroupsAdapted);
                } catch (error) {
                    console.error("Erro ao buscar eventos:", error);
                } finally {
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            },

            formatRecurringFilter(filter) {
                const createDate = (dateString, dateTimeString, fallback) => {
                    if (!dateString) return fallback;
                    const [year, month, day] = dateString.split("-").map(Number);
                    return new Date(year, month - 1, day, ...dateTimeString);
                };

                const onlyFutureEvents = filter?.onlyFutureEvents ?? true;

                const formattedStartDate = createDate(
                    filter?.StartDate?.[0],
                    [0, 0, 0, 0],
                    onlyFutureEvents ? new Date() : new Date(1975, 0, 1, 0, 0, 0, 0)
                );
                const formattedEndDate = createDate(
                    filter?.EndDate?.[0],
                    [23, 59, 59, 999],
                    new Date(2074, 11, 31, 23, 59, 59, 999)
                );

                const today = new Date();
                let minDate =
                    onlyFutureEvents && formattedStartDate < today ? today : formattedStartDate;

                return {
                    min: minDate,
                    max: formattedEndDate,
                    months: filter?.Month?.map((month) => month - 1) ?? [],
                    years: filter?.Year?.map(Number) ?? []
                };
            },

            adaptEvents(events) {
                const adapted = [];
                const monthNames = [
                    "janeiro",
                    "fevereiro",
                    "março",
                    "abril",
                    "maio",
                    "junho",
                    "julho",
                    "agosto",
                    "setembro",
                    "outubro",
                    "novembro",
                    "dezembro"
                ];

                events.sort((a, b) => new Date(a.date) - new Date(b.date));

                events.forEach((event) => {
                    const eventDate = new Date(event.date);
                    const month = eventDate.getMonth() + 1;
                    const year = eventDate.getFullYear();
                    const monthName = monthNames[month - 1];

                    let monthEntry = adapted.find((e) => e.month === month && e.year === year);

                    if (!monthEntry) {
                        monthEntry = { month, year, monthName, events: [] };
                        adapted.push(monthEntry);
                    }
                    monthEntry.events.push(event);
                });

                adapted.sort((a, b) => a.year - b.year || a.month - b.month);
                return adapted;
            },

            parseFilters(filters) {
                const months = this.translatedMonths;
                const monthIndexMap = months.reduce((acc, month, index) => {
                    acc[month] = index + 1;
                    return acc;
                }, {});

                const result = { StartDate: [], EndDate: [], Month: [], Year: [] };

                filters.forEach(({ value }) => {
                    if (value.includes("to")) {
                        const [startDate, endDate] = value.split(" to ");
                        result.StartDate.push(this.formatDateToISO(startDate));
                        result.EndDate.push(this.formatDateToISO(endDate));
                    } else if (value.includes("até")) {
                        const [startDate, endDate] = value.split(" até ");
                        result.StartDate.push(this.formatDateToISO(startDate));
                        result.EndDate.push(this.formatDateToISO(endDate));
                    } else if (months.includes(value)) {
                        result.Month.push(monthIndexMap[value]);
                    } else if (!isNaN(value)) {
                        result.Year.push(value);
                    }
                });

                const queryString = Object.entries(result)
                    .map(([key, value]) => `${key}=${value.join(",")}`)
                    .join("&");

                return { queryString, result };
            },

            formatDateToISO(dateStr) {
                const [day, month, year] = dateStr.split("/");
                return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            },

            formatPermissionsByPage() {
                const permissions = this.getPermissions || [];

                const myconfigPermissions =
                    permissions.find((e) => e.name === "myconfig")?.children || [];

                const exceptionCalendarPermissions =
                    myconfigPermissions.find((e) => e.name === "exception calendars")?.options ||
                    [];

                this.UPDATE_PERMISSIONS_EXCEPTION_LIST(exceptionCalendarPermissions);
            }
        },
        watch: {
            "exception.filters"() {
                this.getEvents();
            },
            "filter.onlyFutureEvents"() {
                this.getEvents();
            }
        }
    };
</script>

<style lang="scss">
    #exception-calendar {
        padding: 16px 0;
        border-radius: 6px;
        background: #fff;
        box-shadow: 0px 4px 24px 0px rgba(58, 46, 38, 0.1);

        @media (max-width: 768px) {
            margin-top: 24px;
        }

        &:has(.no-items) {
            height: calc(100vh - 136px);
        }

        .no-items {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border-radius: 6px;
            margin: 0 16px;
            border: 1px solid #cfc4be;

            .empty-box {
                width: 80px;
                height: 80px;
            }

            .title {
                font-size: 18px;
                font-weight: 600;
                line-height: 26px;
                color: #4c4541;
                margin: 0;
            }

            .subtitle {
                font-size: 14px;
                font-weight: 400;
                line-height: 20px;
                color: #998f8a;
                padding: 4px 0 12px;
                margin: 0;
            }

            .btn-new-event {
                border-color: #974900 !important;
                background-color: #974900 !important;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 8px 21px;
                line-height: 20px;
                letter-spacing: 0.175px;

                .add-icon {
                    width: 14px;
                    height: 14px;
                    fill: #fff;
                }
            }

            @media (max-width: 480px) {
                .empty-box {
                    width: 40px;
                    height: 40px;
                }

                .title {
                    font-size: 14px;
                }

                .subtitle {
                    font-size: 12px;
                    text-align: center;
                    padding: 4px 48px 12px;
                    line-height: 16px;
                }

                .btn-new-event {
                    padding: 3px 14px;
                    color: #fff;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 20px;
                    letter-spacing: 0.15px;

                    .add-icon {
                        width: 12px;
                        height: 12px;
                    }
                }
            }
        }

        .calendar-header {
            padding: 0 16px 8px 0;
            .first-line {
                display: flex;
                justify-content: space-between;
                gap: 8px;

                .title {
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 600;
                }
                .add-events {
                    display: flex;
                    align-items: center;
                    gap: 16px;

                    .buttons {
                        display: flex;
                        gap: 8px;

                        .btn-new-event {
                            border-color: #974900 !important;
                            background-color: #974900 !important;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 8px;
                            width: 100% !important;
                            min-width: 120px;
                            padding: 3px 0 !important;
                            font-size: 12px;
                            font-weight: 600;
                            line-height: 20px;
                            letter-spacing: 0.0125em;
                            box-shadow: none;

                            &.--show-only-icons {
                                min-width: 28px;
                                height: 28px;
                                padding: 0 !important;
                            }

                            .add-icon {
                                width: 12px;
                                height: 12px;
                                fill: #fff;
                            }
                        }

                        .btn-filter {
                            border-color: #974900 !important;
                            background-color: #fff !important;
                            color: #974900 !important;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 6px;
                            width: 100% !important;
                            min-width: 83px;
                            padding: 3px 14px !important;
                            font-size: 12px;
                            font-weight: 600;
                            line-height: 20px;
                            letter-spacing: 0.15px;
                            box-shadow: none;

                            &.--has-filters {
                                min-width: 100px;
                            }

                            &.--show-only-icons {
                                min-width: 28px;
                                height: 28px;
                                padding: 0 !important;
                            }

                            .filter-icon {
                                width: 14px;
                                height: 14px;
                                fill: #974900;
                            }

                            &:hover {
                                background-color: #ffede2 !important;
                            }

                            &:active {
                                background-color: #ffdbc4 !important;
                            }
                        }
                    }

                    @media (max-width: 480px) {
                        flex-direction: column;
                        align-items: flex-start;
                        width: 100%;

                        .buttons {
                            width: 100%;
                        }
                    }
                }

                @media (max-width: 480px) {
                    flex-direction: column;
                    align-items: flex-start;
                }
            }
            .second-line {
                .search-input {
                    display: flex;
                    align-items: center;
                    padding: 16px 0 8px;

                    .div-custom-input {
                        display: flex;
                        align-items: center;
                        border-radius: 4px;
                        width: 100%;
                        overflow: hidden;
                        border: 1px solid #cfc4be;
                        .icon-block {
                            display: flex;
                            align-items: center;
                            padding-left: 14px;
                            height: 14px;
                            .search-icon {
                                width: 14px;
                                height: 14px;
                                fill: #cfc4be;
                            }
                        }
                        .input-search-calendars {
                            width: 100% !important;
                            border: none;
                            overflow: hidden;

                            &.focus {
                                box-shadow: none;
                            }
                            &.b-form-tags {
                                padding: 0 !important;
                                display: flex;
                                align-items: center !important;
                                .b-form-tags-list {
                                    width: 100% !important;
                                    margin-left: 14px !important;
                                    margin-top: 0;
                                    height: 100% !important;
                                    flex-wrap: nowrap !important;
                                    display: flex !important;
                                    align-items: center !important;
                                    overflow: scroll;
                                    overflow-y: hidden;
                                    cursor: grabbing;
                                    cursor: -webkit-grabbing;

                                    &::-webkit-scrollbar {
                                        display: none !important;
                                    }
                                    .b-form-tags-field {
                                        margin: 9px 0;
                                    }
                                }
                            }

                            .b-form-tags-button {
                                display: none;
                            }

                            .b-form-tag {
                                background-color: #974900;
                                margin-right: 0.6rem;
                                padding: 2px 8px !important;
                                font-size: 13px;
                            }
                        }
                    }
                }

                .filter-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 2px 0 10px 0;
                    overflow-x: auto;

                    .btn-clear {
                        border-color: #974900 !important;
                        background-color: #974900 !important;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 20px;
                        letter-spacing: 0.15px;
                        height: 28px;
                        padding: 2px 14px;
                        box-shadow: none;
                    }

                    .filter {
                        display: flex;
                        padding: 6px 12px;
                        align-items: center;
                        gap: 8px;
                        border-radius: 8px;
                        background: #ffdbc4;
                        color: #974900;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 20px;
                        letter-spacing: 0.15px;
                        white-space: nowrap;

                        .remove-icon {
                            cursor: pointer;
                            width: 18px;
                            height: 18px;
                            fill: #974900;
                        }
                    }

                    &::-webkit-scrollbar {
                        height: 3px;
                    }

                    &::-webkit-scrollbar-track {
                        background: #fff;
                    }

                    &::-webkit-scrollbar-thumb {
                        background-color: #cfc4be;
                        border-radius: 9px;
                    }
                }
            }
        }

        .event-block {
            max-height: calc(100vh - 268px);
            overflow: auto;
            padding-right: 4px;
            margin-right: 8px;

            @media (max-width: 768px) {
                max-height: calc(100vh - 300px);
            }

            @media (max-width: 480px) {
                max-height: calc(100vh - 300px);
            }
        }

        .calendar-body {
            padding: 0 0 0 16px;
        }

        .block-no-results {
            height: calc(100vh - 312px);
            padding-right: 16px;
            .no-results {
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 16px;
                border-radius: 6px;
                border: 1px solid #cfc4be;

                svg {
                    width: 16px;
                    height: 16px;
                    fill: #998f8a;
                }

                .text {
                    margin: 0;
                    color: #998f8a;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 26px;
                }

                @media (max-width: 480px) {
                    svg {
                        width: 12px;
                        height: 12px;
                    }
                    .text {
                        font-size: 12px;
                        line-height: 16px;
                    }
                }
            }

            @media (max-width: 768px) {
                height: calc(100vh - 345px);
            }

            @media (max-width: 480px) {
                height: calc(100vh - 415px);
            }
        }

        &::-webkit-scrollbar {
            height: 3px;
        }

        &::-webkit-scrollbar-track {
            background: #fff;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #cfc4be;
            border-radius: 9px;
        }
    }
</style>
